import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { Status } from '@elevenlabs/client';

const ELEVENLABS_API = 'https://api.elevenlabs.io';

export interface ChatMessage {
  role: 'agent' | 'user';
  text: string;
}

const AGENT_ID = 'agent_0201kqxxammqfrqa9x9vbsxk95cm';

@Injectable({
  providedIn: 'root',
})
export class ElevenLabsWidgetService {
  private conv: import('@elevenlabs/client').Conversation | null = null;
  private starting = false;

  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private statusSubject = new BehaviorSubject<Status>('disconnected');
  private visibleSubject = new BehaviorSubject<boolean>(false);
  private canSendFeedbackSubject = new BehaviorSubject<boolean>(false);
  private agentWritingSubject = new BehaviorSubject<boolean>(false);

  readonly messages$ = this.messagesSubject.asObservable();
  readonly status$ = this.statusSubject.asObservable();
  readonly visible$ = this.visibleSubject.asObservable();
  readonly canSendFeedback$ = this.canSendFeedbackSubject.asObservable();
  readonly isAgentWriting$ = this.agentWritingSubject.asObservable();

  constructor(private zone: NgZone) {}

  get isVisible(): boolean {
    return this.visibleSubject.value;
  }

  async open(dynamicVariables: Record<string, string | number | boolean>): Promise<void> {
    if (this.conv || this.starting) return;

    this.starting = true;
    this.visibleSubject.next(true);
    this.messagesSubject.next([]);

    try {
      const { Conversation } = await import('@elevenlabs/client');
      this.conv = await Conversation.startSession({
        agentId: AGENT_ID,
        dynamicVariables,
        textOnly: true,
        onMessage: ({ message, source }) => {
          if (source !== 'ai') return; // user messages added optimistically in sendMessage
          this.zone.run(() => {
            this.agentWritingSubject.next(false);
            this.messagesSubject.next([
              ...this.messagesSubject.value,
              { role: 'agent', text: message },
            ]);
          });
        },
        onStatusChange: ({ status }) => {
          this.zone.run(() => this.statusSubject.next(status));
        },
        onCanSendFeedbackChange: ({ canSendFeedback }) => {
          this.zone.run(() => this.canSendFeedbackSubject.next(canSendFeedback));
        },
        onDisconnect: () => {
          this.zone.run(() => {
            this.conv = null;
            this.starting = false;
            this.statusSubject.next('disconnected');
            this.canSendFeedbackSubject.next(false);
            this.agentWritingSubject.next(false);
            this.visibleSubject.next(false);
          });
        },
        onError: (message) => {
          console.error('[NurIA]', message);
          this.zone.run(() => {
            this.conv = null;
            this.starting = false;
            this.statusSubject.next('disconnected');
            this.agentWritingSubject.next(false);
          });
        },
      });
    } catch (e) {
      console.error('[NurIA] startSession failed', e);
      this.zone.run(() => {
        this.conv = null;
        this.starting = false;
        this.statusSubject.next('disconnected');
        this.visibleSubject.next(false);
      });
    } finally {
      this.starting = false;
    }
  }

  async close(): Promise<void> {
    if (!this.conv) return;
    await this.conv.endSession();
    this.conv = null;
    this.visibleSubject.next(false);
    this.statusSubject.next('disconnected');
    this.canSendFeedbackSubject.next(false);
  }

  toggle(dynamicVariables: Record<string, string | number | boolean>): void {
    if (this.isVisible) {
      this.close();
    } else {
      this.open(dynamicVariables);
    }
  }

  /** Per-message like/dislike — only works when canSendFeedback$ is true */
  sendFeedback(like: boolean): void {
    this.conv?.sendFeedback(like);
    this.canSendFeedbackSubject.next(false);
  }

  /** Overall conversation rating (1–5) + optional comment — works any time during session */
  async sendOverallFeedback(rating: number, comment?: string): Promise<void> {
    const conversationId = this.conv?.getId();
    if (!conversationId) return;
    await fetch(`${ELEVENLABS_API}/v1/convai/conversations/${conversationId}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating, comment: comment ?? '' }),
    });
  }

  sendMessage(text: string): void {
    if (!this.conv) return;
    this.messagesSubject.next([
      ...this.messagesSubject.value,
      { role: 'user', text },
    ]);
    this.agentWritingSubject.next(true);
    this.conv.sendUserMessage(text);
  }
}
